import { useApp } from "../context/AppContext";
import TableCard from "../components/TableCard";
import WaitingTabs from "../components/WaitingTabs";
import { Activity, IndianRupee, Users } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "../lib/format";

export default function TablesPage() {
  const { tables, loading, fetchDashboardStats } = useApp();
  const { isAdmin, isManager } = useAuth();
  const [todayStats, setTodayStats] = useState({ revenue: 0, customers: 0 });

  const activeCount = tables.filter((t) => t.status === "occupied").length;
  const regularTables = tables.filter((t) => t.table_type !== "playstation");
  const psTables = tables.filter((t) => t.table_type === "playstation");

  // Refresh today's revenue/sessions on initial load and whenever a table is
  // started or stopped (occupancy change) — no background polling. Starting or
  // stopping a table refetches `tables`, which moves `activeCount` and re-runs this.
  useEffect(() => {
    if (!isAdmin) return;
    const fetchStats = async () => {
      const data = await fetchDashboardStats();
      if (data) {
        setTodayStats({
          revenue: data.today_revenue,
          customers: data.today_customers,
        });
      }
    };
    fetchStats();
  }, [isAdmin, fetchDashboardStats, activeCount]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="tables-page">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-zinc-100">
          Tables
        </h1>
        <p className="text-zinc-500 text-sm mt-1">Manage your tables in real-time</p>
      </div>

      {/* Quick Stats */}
      <div className={`grid ${isManager ? "grid-cols-1" : "grid-cols-3"} gap-3 md:gap-4 mb-8`} data-testid="quick-stats">
        <div className="bg-[#18181b] border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-zinc-400 mb-1">
            <Activity className="w-4 h-4 text-green-400" />
            <span className="text-xs uppercase tracking-wider">Active</span>
          </div>
          <p className="font-heading text-3xl font-bold text-zinc-100" data-testid="stat-active">
            {activeCount}
          </p>
        </div>
        {isAdmin && (
          <div className="bg-[#18181b] border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-zinc-400 mb-1">
              <IndianRupee className="w-4 h-4 text-green-400" />
              <span className="text-xs uppercase tracking-wider">Today</span>
            </div>
            <p className="font-heading text-3xl font-bold text-zinc-100" data-testid="stat-revenue">
              ₹{formatMoney(todayStats.revenue)}
            </p>
          </div>
        )}
        {isAdmin && (
          <div className="bg-[#18181b] border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-zinc-400 mb-1">
              <Users className="w-4 h-4 text-green-400" />
              <span className="text-xs uppercase tracking-wider">Sessions</span>
            </div>
            <p className="font-heading text-3xl font-bold text-zinc-100" data-testid="stat-sessions">
              {todayStats.customers}
            </p>
          </div>
        )}
      </div>

      <WaitingTabs />

      {/* Tables Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {regularTables.map((table) => (
          <TableCard key={table.id} table={table} />
        ))}
      </div>

      {/* PlayStations — separate section */}
      {psTables.length > 0 && (
        <div className="mt-10" data-testid="playstation-section">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-zinc-100 mb-4">
            PlayStations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {psTables.map((table) => (
              <TableCard key={table.id} table={table} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
